import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { PullDataService } from '../../service/PullDataService';
import { useAuthStore } from '../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import SearchableSelect from '../SearchableSelect';
import TagInput from '../Test/TagInput';
import Label from '../UI/Label/Label';

const LoadingPlaceholder = ({
  theme,
  label,
}: {
  theme?: string;
  label: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      py: '8px',
    }}
  >
    <CircularProgress size={16} sx={{ color: theme === 'dark' ? '#D0D5DD' : '#667085' }} />
    <Typography
      sx={{
        fontSize: '13px',
        color: theme === 'dark' ? '#98A2B3' : '#667085',
      }}
    >
      {label}
    </Typography>
  </Box>
);

interface MicrosoftPowerPointFile {
  id: string;
  name: string;
}

interface PullMicrosoftPowerPointDataProps {
  formData: any;
  onChange: (e: { target: { name: string; value: any } }) => void;
  onChangeSelect: (e: { target: { name: string; value: any } }) => void;
  data: any;
  nodeId: string;
  nodeLabel: string;
}

const PullMicrosoftPowerPointData: React.FC<PullMicrosoftPowerPointDataProps> = ({
  formData,
  onChange,
  onChangeSelect,
  data,
  nodeId,
  nodeLabel,
}) => {
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);

  const [powerPointFiles, setPowerPointFiles] = useState<MicrosoftPowerPointFile[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string>(
    data?.microsoftPowerPointFileId ?? '',
  );
  const [loadingFiles, setLoadingFiles] = useState(false);

  const loadPowerPointFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const response = await PullDataService.getMicrosoftPowerPointFiles();
      setPowerPointFiles(response.data.files ?? []);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    } finally {
      setLoadingFiles(false);
    }
  }, [setNotification]);

  useEffect(() => {
    loadPowerPointFiles();
  }, [loadPowerPointFiles]);

  const fileOptions = powerPointFiles.map(f => ({
    label: f.name,
    value: f.id,
  }));

  const searchableSelectSx = {
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    '& .MuiSvgIcon-root': {
      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
    },
    borderRadius: '8px',
    border: 'none',
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
  };

  const menuPropsSx = {
    PaperProps: {
      sx: {
        '& .Mui-selected': {
          backgroundColor: user?.theme === 'dark' ? '#667085' : '#D0D5DD',
        },
        backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
      },
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '360px',
      }}
    >
      {loadingFiles ? (
        <LoadingPlaceholder theme={user?.theme} label="Loading presentations..." />
      ) : (
        <>
          <Label
            labelStyle={{
              fontSize: '14px',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              fontWeight: 400,
            }}
            labelName="Presentation"
            isAdvanced
          />

          <SearchableSelect
            options={fileOptions}
            label="Select a PowerPoint file"
            onChange={value => {
              const fileId = value ? value.value : '';
              setSelectedFileId(fileId);
              setTimeout(() => {
                onChangeSelect({
                  target: { name: 'microsoftPowerPointFileId', value: fileId },
                });
              }, 0);
            }}
            value={fileOptions.find(o => o.value === selectedFileId) || null}
            className="nodrag"
            style={{ width: 100, marginBottom: '0px' }}
            sx={searchableSelectSx}
            MenuProps={menuPropsSx}
          />

          <TagInput
            placeholder="Select slides [1, 2-5, 8]"
            onChange={onChange}
            name="slidesFilter"
            value={formData.slidesFilter ?? ''}
            isAdvanced
            showCopy
            dataConnected={data?.slidesFilterData || []}
            removeIcons
            labelName="Select Slides (Optional)"
            isCollapsed
            nodeId={nodeId}
            handleId="slidesFilter"
            nodeLabel={nodeLabel}
            labelStyle={{
              fontSize: '14px',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              fontWeight: 400,
            }}
            advancedCollapsed
          />
        </>
      )}
    </Box>
  );
};

export default PullMicrosoftPowerPointData;
